import React, { useState } from "react";
import { Feed } from "../../types/api/Feed";
import { SelectedFile } from "../../types/PostSelection";
import { AlertCircle, Check } from "react-feather";
import { RequirementsType } from "../Feed/Requirements";
import { ImageThumbnail } from "../ImageThumbnail";
import { FieldValues } from "react-hook-form";
import { UseFormSetValue } from "react-hook-form";
import cx from "classnames";
import { getSelectedFileKey } from "../../api/helper/resolvePostMediaSelections";

type TypeMapping = {
  [key: string]: {
    name: string;
    type: string;
    photo_type?: string;
    video_type?: string;
  };
};

type Issue = {
  type: "MINETYPE" | "FILESIZE" | "ITEMS" | "DURATION" | "TEXT_LENGTH";
  file?: SelectedFile;
  description: string;
  maxItems?: number;
};

export function ContentCheck({
  feed,
  selectedFiles,
  userSelectedFiles,
  postType,
  setValue,
  content,
  hashtags,
  location,
  formattedContent,
}: {
  feed?: Feed;
  selectedFiles?: SelectedFile[];
  userSelectedFiles?: SelectedFile[];
  postType?: string;
  setValue: UseFormSetValue<FieldValues>;
  content?: string;
  hashtags?: string[];
  location?: string;
  formattedContent: string;
}) {
  const [showSelection, setShowSelection] = useState(false);
  const [selectedCount, setSelectedCount] = useState<number | null>(null);
  const [filesToSelect, setFilesToSelect] = useState<SelectedFile[]>([]);
  const effectiveSelectedFiles =
    userSelectedFiles && userSelectedFiles.length > 0
      ? userSelectedFiles
      : selectedFiles ?? [];

  const setItemsIssue = (itemLimitIssue: Issue) => {
    setSelectedCount(itemLimitIssue.maxItems || null);
    const files = JSON.parse(
      JSON.stringify(effectiveSelectedFiles)
    ) as SelectedFile[];

    console.log("USER SELECTED FILES", userSelectedFiles);

    if (!userSelectedFiles) {
      for (let i = 0; i < files.length; i++) {
        files[i].isSelected = i < itemLimitIssue.maxItems!;
      }
    } else {
      for (const file of files) {
        file.isSelected = false;
      }
      for (const file of userSelectedFiles) {
        const selectionKey = getSelectedFileKey(file);
        const index = files.findIndex(
          (candidate) => getSelectedFileKey(candidate) === selectionKey
        );
        if (index !== -1) {
          files[index].isSelected = true;
          files[index].selectedAt = file.selectedAt;
        }
      }
    }
    setFilesToSelect(files);
    setShowSelection(true);
  };

  if (!feed || !feed.requirements || !selectedFiles || !postType) {
    return null;
  }

  const issues = validateMedia(feed, effectiveSelectedFiles, postType);

  const textIssue = validateText(feed, formattedContent);
  if (textIssue) {
    issues.push(textIssue);
  }

  const onFilesChange = (files: SelectedFile[]) => {
    console.log("USER SELECTED FILES", files);
    setValue(`${feed.slug}.selected_files`, files);
  };

  if (issues.length > 0) {
    return (
      <div className="space-y-2">
        {issues.map((i: Issue, index: number) => {
          const itemLimitIssue = i.type === "ITEMS";
          const fixed = itemLimitIssue && (userSelectedFiles ?? []).length > 0;

          return (
            <div
              key={index}
              role="alert"
              className={cx(
                "alert alert-soft",
                fixed ? "alert-success" : "alert-error"
              )}
            >
              {fixed ? <Check /> : <AlertCircle />}
              <div className="text-sm">{i.description}</div>

              {itemLimitIssue && (!showSelection || selectedCount === null) && (
                <button
                  className={cx(
                    "btn btn-sm btn-outline",
                    fixed ? "btn-success" : "btn-error"
                  )}
                  onClick={() => setItemsIssue(i)}
                >
                  Select Files
                </button>
              )}
            </div>
          );
        })}
        {showSelection && selectedCount && (
          <ItemLimitIssue
            selectedCount={selectedCount}
            filesToSelect={filesToSelect}
            setFilesToSelect={setFilesToSelect}
            setShowSelection={setShowSelection}
            onFilesChange={onFilesChange}
          />
        )}
      </div>
    );
  }

  return null;
}

function ItemLimitIssue({
  selectedCount,
  filesToSelect,
  setFilesToSelect,
  setShowSelection,
  onFilesChange,
}: {
  selectedCount: number;
  filesToSelect: SelectedFile[];
  setFilesToSelect: React.Dispatch<React.SetStateAction<SelectedFile[]>>;
  setShowSelection: React.Dispatch<React.SetStateAction<boolean>>;
  onFilesChange: (files: SelectedFile[]) => void;
}) {
  const [leftToSelect, setLeftToSelect] = useState<number>(0);

  const selected = filesToSelect
    .filter((f: SelectedFile) => f.isSelected)
    .sort((a, b) => a.selectedAt! - b.selectedAt!);
  const isSelectedCount = selected.length;
  const maxReached = isSelectedCount >= selectedCount;

  console.log("USER SELECTED FILES IN ORDER", selected);

  return (
    <fieldset className="fieldset bg-base-200 border-error rounded-box border p-4">
      <legend className="fieldset-legend px-2">
        Select {selectedCount} photos/videos ({leftToSelect} left to select)
      </legend>

      <div className="flex flex-wrap gap-4 mb-4">
        {filesToSelect.map((file: SelectedFile, index: number) => (
          <ImageThumbnail
            key={file.id || index}
            id={file.id || index}
            title={file.name}
            url={file.url}
            minetype={file.mine_type}
            isSelected={file.isSelected}
            selectedOrder={
              selected.findIndex((f: SelectedFile) => f.id === file.id) + 1
            }
            onSelect={() => {
              console.log("ON SELECT", index, file);
              const newFilesToSelect = JSON.parse(
                JSON.stringify(filesToSelect)
              );
              const isSelected = !file.isSelected;
              newFilesToSelect[index] = {
                ...file,
                isSelected: maxReached ? false : isSelected,
                selectedAt: isSelected ? Date.now() : undefined,
              };
              const newIsSelectedCount = newFilesToSelect.filter(
                (f: SelectedFile) => f.isSelected
              ).length;
              setLeftToSelect(selectedCount - newIsSelectedCount);
              console.log("FILES TO SELECT", newFilesToSelect);
              setFilesToSelect(() => [...newFilesToSelect]);
            }}
            className={cx(maxReached && "cursor-not-allowed")}
          />
        ))}
      </div>

      <div className="flex justify-start items-center gap-4">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => {
            onFilesChange(selected);
            setShowSelection(false);
          }}
        >
          <Check />
          Confirm Selection
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShowSelection(false)}
        >
          Cancel
        </button>
      </div>
    </fieldset>
  );
}

function validateMedia(
  feed: Feed,
  selectedFiles: SelectedFile[],
  postType: string
) {
  // TODO: Add requirements for videos
  // TODO: Add requirements for text
  const typeMappings = feed.type_mapping as TypeMapping;
  const requirements = feed.requirements as RequirementsType;
  const mappedType =
    typeMappings?.[postType]?.type ?? typeMappings?.[postType]?.photo_type;
  const globalRequirement = (requirements?.PHOTO?.overrides?.[mappedType] ??
    requirements?.VIDEO?.overrides?.[mappedType] ??
    requirements?.PHOTO ??
    requirements?.VIDEO) as
    | RequirementsType["PHOTO"]
    | RequirementsType["VIDEO"];

  let hasNumberIssue = false;

  const issues: Issue[] = [];

  // Check total items limit
  if (
    requirements?.total_max_items &&
    selectedFiles.length > requirements.total_max_items
  ) {
    hasNumberIssue = true;
    issues.push({
      type: "ITEMS",
      file: selectedFiles[0],
      description: `This feed can only have ${requirements.total_max_items} photos or videos`,
      maxItems: requirements.total_max_items,
    });
  }

  // Check type-specific item limits
  if (
    globalRequirement?.max_items &&
    selectedFiles.length > globalRequirement.max_items &&
    !hasNumberIssue
  ) {
    issues.push({
      type: "ITEMS",
      file: selectedFiles[0],
      description: `This feed can only have ${requirements.total_max_items} photos`,
      maxItems: globalRequirement.max_items,
    });
  }

  // Check each file's requirements
  for (const file of selectedFiles) {
    if (file.mine_type?.includes("image")) {
      const photoRequirement =
        requirements?.PHOTO?.overrides?.[mappedType] ?? requirements?.PHOTO;

      // Check MIME type
      if (!photoRequirement?.minetype?.includes(file.mine_type)) {
        issues.push({
          type: "MINETYPE",
          file,
          description: `The file "${file.name}" has the wrong MIME type "${
            file.mine_type
          }". Supported types: ${photoRequirement?.minetype?.join(", ")}`,
        });
      }

      // Check file size
      if (
        photoRequirement?.max_filesize &&
        file.size &&
        file.size > photoRequirement.max_filesize * 1024 * 1024
      ) {
        issues.push({
          type: "FILESIZE",
          file,
          description: `The file "${file.name}" exceeds the maximum file size of ${photoRequirement.max_filesize} MB`,
        });
      }
    }

    if (file.mine_type?.includes("video")) {
      const videoRequirement =
        requirements?.VIDEO?.overrides?.[mappedType] ?? requirements?.VIDEO;

      // Check video duration if applicable
      if (
        videoRequirement?.min_duration &&
        file.duration &&
        file.duration < videoRequirement.min_duration
      ) {
        issues.push({
          type: "DURATION",
          file,
          description: `The file "${file.name}" duration is below the min duration of ${videoRequirement.min_duration} seconds`,
        });
      }

      if (
        videoRequirement?.max_duration &&
        file.duration &&
        file.duration > videoRequirement.max_duration
      ) {
        issues.push({
          type: "DURATION",
          file,
          description: `The file "${file.name}" duration is above the max duration of ${videoRequirement.max_duration} seconds`,
        });
      }
    }
  }

  return issues;
}

export function validateText(
  feed?: Feed,
  formattedContent?: string
): Issue | null {
  if (!feed) return null;

  const requirements = feed.requirements as RequirementsType;
  const maxLength = requirements?.TEXT?.length;

  const totalLength = formattedContent?.length;

  if (maxLength && totalLength && totalLength > maxLength) {
    return {
      type: "TEXT_LENGTH",
      description: `This feed can only have ${maxLength} characters (Current length: ${totalLength})`,
    };
  }

  return null;
}
